import { useCallback, useEffect, useRef, useState } from 'react';
import { estEchafaudage, REFUS_ACTIVATION, type OrigineEchafaudage } from '../../src/partage/echafaudage.js';
import { actionsPour, motifListeVide, type EtatParcelle } from '../../src/partage/actions-parcelle.js';
import { api, ErreurApi, type ControlesPlan, type ControlesSituation,
  type EditionsArpentage, type EditionsPlan, type EditionsSituation,
  type EtatArpentage, type ProcedureParcelle,
  type ResultatRapprochementFiscal } from './api';
import { libelleUsageBati, surfaceEnFrancais } from '../../src/partage/libelles';
import { paletteCarte, alpha } from './palette-carte.js';
import { ChronologieJuridique } from './procedures';
import { ImprimerLaPlanche } from './panneau-cadastral';
import { SectionProjection } from './projection';

/**
 * FICHE PARCELLE (§5, §10) — tout ce à quoi l'agent a droit, et RIEN de plus.
 *
 * ⚠️ CE PANNEAU N'INVENTE AUCUN CHAMP. Il affiche ce que le service rend, y
 * compris ses refus : quand `droits` revient `{ masque: true }`, on écrit que
 * c'est masqué et pourquoi. Un panneau qui n'afficherait rien dans ce cas
 * ferait lire « cette parcelle n'a pas de titulaire » — ce qui est faux, et
 * c'est le pire contresens possible dans un registre foncier.
 */

/* Délègue au formateur unique de `partage/libelles` : cette fonction rendait
   « 5302 m² » sans séparateur là où la table attributaire rendait
   « 5 302,2 m² » pour la même valeur. */
const metres = (v: unknown): string => surfaceEnFrancais(v);

function dateFr(v: unknown): string {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('fr-FR');
}

/** L'état de vérification, en QUATRE valeurs — jamais un booléen. */
function etatVerification(q: any): { libelle: string; classe: string; explication: string } {
  if (q?.verifie_le && q?.opposable) {
    return { libelle: 'Vérifiée', classe: 'vert', explication: `Contrôlée le ${dateFr(q.verifie_le)}, origine ${q.origine}.` };
  }
  if (q?.origine === 'maille_systematique') {
    return {
      libelle: 'Non vérifiable', classe: 'gris',
      explication: "Maille présomptive de 25 m issue du quadrillage : elle situe, elle ne titre pas. Un levé est requis.",
    };
  }
  if (q?.origine === 'derive_bati') {
    return { libelle: 'Non vérifiable', classe: 'gris', explication: "Contour dérivé du bâti : indicatif, non opposable." };
  }
  if (q?.verifie_le && !q?.opposable) {
    return { libelle: 'Incohérente', classe: 'rouge', explication: "Contrôlée mais non opposable en l'état." };
  }
  return { libelle: 'Déclarée', classe: 'ambre', explication: "Aucun contrôle enregistré à ce jour." };
}

/**
 * LE TIROIR DE LA CARTE — l'enveloppe, et rien d'autre.
 *
 * ⚠️ IL SURVIT PARCE QU'IL A UN SENS *SUR LA CARTE* : on sélectionne une
 * parcelle et on veut la lire sans quitter le plan. Ailleurs, il se posait
 * par-dessus des écrans qui parlaient déjà de la même affaire — c'est ce que
 * le fondateur a désigné en disant « l'encoche de droite ne devrait pas
 * exister ». Le corps, lui, est allé vivre dans l'écran du dossier.
 */
/**
 * LA PARCELLE ET SON BÂTI, DESSINÉS.
 *
 * ⚠️ LA TRAME DIT CE QUE VAUT LE TRACÉ. Une limite présomptive figurée au trait
 * plein est indiscernable d'un bornage — et 987 754 parcelles sur 988 686 le
 * sont. Le tireté n'est pas une décoration : c'est la différence entre un
 * document d'information et un titre officieux.
 *
 * ⚠️ ET LE CADRAGE PREND L'UNION parcelle + bâti. Un bâtiment qui déborde de sa
 * parcelle est un fait courant du registre ; le cadrer hors champ le rendrait
 * invisible sans rien dire, alors que ce débordement est souvent l'information
 * qu'on cherche.
 */
function TraceParcelle({ geometrie, batiments }: { geometrie: any; batiments: any[] }) {
  const toile = useRef<HTMLCanvasElement | null>(null);
  const anneauxDe = (g: any): number[][][] => {
    if (!g) return [];
    if (g.type === 'Polygon') return [g.coordinates?.[0] ?? []];
    if (g.type === 'MultiPolygon') return (g.coordinates ?? []).map((p: any) => p?.[0] ?? []);
    return [];
  };

  useEffect(() => {
    const c = toile.current; if (!c || !geometrie) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    const style = getComputedStyle(document.documentElement);
    const jeton = (n: string, repli: string) => style.getPropertyValue(n).trim() || repli;
    const P = paletteCarte();

    const dessiner = () => {
      const r = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      c.width = Math.max(1, Math.round(r.width * dpr));
      c.height = Math.max(1, Math.round(r.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, r.width, r.height);

      let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
      const avaler = (g: any) => {
        for (const an of anneauxDe(g)) for (const q of an) {
          if (q[0]! < x0) x0 = q[0]!; if (q[0]! > x1) x1 = q[0]!;
          if (q[1]! < y0) y0 = q[1]!; if (q[1]! > y1) y1 = q[1]!;
        }
      };
      avaler(geometrie);
      for (const b of batiments) avaler(b.contour);
      if (x0 > x1) return;

      const kx = Math.cos(((y0 + y1) / 2) * Math.PI / 180), m = 16;
      const k = Math.min((r.width - m * 2) / ((x1 - x0) * kx || 1e-9),
                         (r.height - m * 2) / ((y1 - y0) || 1e-9));
      const ox = (r.width - (x1 - x0) * kx * k) / 2, oy = (r.height - (y1 - y0) * k) / 2;
      const px = (q: number[]) => [ox + (q[0]! - x0) * kx * k, oy + (y1 - q[1]!) * k] as const;
      const tracer = (g: any) => {
        ctx.beginPath();
        for (const an of anneauxDe(g)) {
          an.forEach((q, i2) => { const t = px(q); i2 ? ctx.lineTo(t[0], t[1]) : ctx.moveTo(t[0], t[1]); });
          ctx.closePath();
        }
      };

      tracer(geometrie);
      ctx.fillStyle = alpha(P.maille, .45); ctx.fill('evenodd');
      ctx.strokeStyle = jeton('--or', '#f5f5f5'); ctx.lineWidth = 2; ctx.stroke();

      for (const b of batiments) {
        tracer(b.contour);
        ctx.fillStyle = alpha(P.bati, .90); ctx.fill('evenodd');
        ctx.strokeStyle = alpha(P.batiTrait, .90); ctx.lineWidth = 1; ctx.stroke();
      }

      // Échelle graphique : le dessin se mesure, ou ne sert à rien.
      const metresParPixel = 111320 / k;
      const PAS = [2, 5, 10, 20, 50, 100, 200, 500];
      const pas = PAS.find((x) => x >= 70 * metresParPixel) ?? PAS[PAS.length - 1]!;
      const bx = 10, by = r.height - 12;
      ctx.fillStyle = jeton('--texte', '#f5f5f5');
      ctx.fillRect(bx, by, pas / metresParPixel, 2);
      ctx.font = '500 10px ui-monospace, monospace'; ctx.textAlign = 'left';
      ctx.fillStyle = jeton('--texte-2', '#a1a1a1');
      ctx.fillText(`${pas} m`, bx, by - 4);
    };
    dessiner();
    window.addEventListener('resize', dessiner);
    return () => window.removeEventListener('resize', dessiner);
  }, [geometrie, batiments]);

  if (!geometrie) {
    // ⚠️ DIRE L'ABSENCE plutôt que rendre un cadre vide : une toile blanche se
    // lit « le dessin a échoué », pas « cette parcelle n'a pas de tracé ».
    return <p className="vide">Aucun tracé au registre pour cette parcelle.</p>;
  }
  return (
    <>
      <canvas ref={toile}
              style={{ width: '100%', height: '34vh', minHeight: 220, display: 'block',
                       border: '1px solid var(--bord)', borderRadius: 6 }} />
      <p className="mention">
        Trait doré : limite de la parcelle au registre · aplat magenta :
        {' '}{batiments.length === 0 ? 'aucune emprise bâtie rattachée' : `${batiments.length} emprise(s) bâtie(s)`}
      </p>
    </>
  );
}


/**
 * Des mètres tant qu'ils se comptent, des kilomètres au-delà.
 * ⚠️ « 12912 m » se lit mal et se sous-estime ; « 12,9 km » dit tout de suite
 * qu'on a changé de quartier.
 */
function formerDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1).replace('.', ',')} km`;
}

export function PanneauParcelle({
  identifiant, onFermer, ...reste
}: Parameters<typeof CorpsParcelle>[0] & { onFermer: () => void }) {
  /* D.1504 — la désignation mène. Le panneau s'ouvre sur l'identifiant (seule
     chose connue avant la fiche), puis le corps remonte la référence scellée
     dès qu'elle est lue : « 003-AAK-0326-0001-7K » coiffe le panneau, et
     GN-PAR-… reste lisible dans l'aperçu au rang d'identifiant interne. */
  const [titre, setTitre] = useState<string | null>(null);
  return (
    <aside className="panneau">
      <EnTete titre={titre ?? identifiant} onFermer={onFermer} />
      {/* `onTitre` APRÈS l'étalement : l'en-tête appartient au panneau, un
          hôte qui le passerait par `reste` l'écraserait sans le voir. */}
      <CorpsParcelle identifiant={identifiant} {...reste} onTitre={setTitre} />
    </aside>
  );
}

/**
 * LE CORPS DE L'AFFAIRE — tout ce que le registre sait d'une parcelle.
 *
 * ⚠️⚠️ SÉPARÉ DE SON ENVELOPPE, ET C'EST TOUTE LA DEMANDE DU FONDATEUR :
 * « quand on ouvre un dossier on doit avoir tous les éléments : aperçu,
 * procédures, etc. » Ces six onglets ne vivaient QUE dans le tiroir de la
 * carte. L'écran d'un dossier montrait la procédure — demande, récépissé, fil,
 * actes — et rien du bien : ni son aperçu, ni ses droits, ni ses documents, ni
 * son historique. Pour voir l'affaire entière il fallait ouvrir une SECONDE
 * surface, qui venait se poser PAR-DESSUS la première.
 *
 * ⚠️ UN COMPOSANT, DEUX HÔTES — jamais deux copies. Le tiroir de la carte et
 * l'écran du dossier affichent désormais le MÊME corps. Recopier les six
 * onglets dans l'écran du dossier aurait produit deux exemplaires qui
 * divergeraient au premier correctif appliqué à un seul : c'est le motif le
 * plus cher de cet écosystème, et il est écrit en tête de `sonde-gabarits.sh`.
 *
 * ⚠️ IL CHARGE SA PROPRE FICHE. L'écran du dossier connaît l'identifiant de la
 * parcelle, pas sa fiche complète : lui faire porter la requête l'obligerait à
 * connaître un contrat qui ne le regarde pas, et le tiroir de la carte
 * devrait alors la charger de son côté — deux chemins pour une même lecture.
 */
export function CorpsParcelle({
  identifiant, onOuvrirDossier, metier = null, permissions = [], onOuvrirRubrique,
  peutActiver = false, peutRattacherFiscal = false, peutReferencer = false,
  peutValider = false, peutTraiterAnomalie = false, onSection, onTitre,
}: {
  identifiant: string;
  /**
   * La section de CETTE fiche, dite au parent — exclusion D.995 : quand le
   * tiroir montre déjà la planche d'une section, l'inspecteur de la carte se
   * tait au lieu d'afficher le même choix une seconde fois. Rappelée à `null`
   * au démontage et à chaque changement de parcelle, sinon le panneau se
   * tairait sur la foi d'une fiche qu'on ne montre plus.
   */
  onSection?: (s: { code: string; circ: number } | null) => void;
  /**
   * Le TITRE du panneau, dit au parent une fois la fiche lue — D.1504, « tout
   * doit être comme ça » : le fonds s'annonce par sa désignation cadastrale
   * (003-AAK-0326…), jamais par l'identifiant interne, qu'aucun plan ne porte.
   * Même contrat que `onSection` : rappelé à `null` au démontage, sinon le
   * panneau garderait le titre d'une fiche qu'on ne montre plus.
   */
  onTitre?: (titre: string | null) => void;
  /**
   * Ouvre le dossier de la procédure citée — la porte qui manquait.
   *
   * ⚠️ `procedures.tsx` PORTE DEPUIS TOUJOURS le bouton « Ouvrir le dossier »,
   * conditionné à ce rappel. Personne ne le lui passait : mesuré le
   * 14/09/2026, `onOuvrirDossier` comptait 0 occurrence dans ce fichier. Le
   * panneau NOMMAIT donc le dossier de l'affaire — « GN-DOS-000000005 » écrit
   * en toutes lettres — sans offrir aucun chemin pour l'atteindre. Un bouton
   * absent ne fait pas d'erreur : il n'existe pas, et l'agent conclut que le
   * dossier n'est pas consultable depuis la parcelle.
   */
  onOuvrirDossier?: (reference: string) => void;
  /** cad.parcelle:modifier — sans elle le service refusera POST …/activer. */
  /**
   * LE MÉTIER DE L'AGENT ET SON HABILITATION — de quoi DÉCIDER, au lieu d'un
   * booléen par action. Chaque geste ajouté demandait jusqu'ici un fil de plus
   * depuis `app.tsx` ; c'est ce coût-là qui empêchait l'onglet d'exister.
   */
  metier?: string | null;
  /** Ouvre une rubrique du rail — ce que fait une action offerte. */
  onOuvrirRubrique?: (rubrique: string, parcelle?: string) => void;
  permissions?: readonly { ressource: string; operation: string }[];
  peutActiver?: boolean;
  peutReferencer?: boolean;
  /** lien.objet_fiscal:creer — sans elle le service refusera POST /graphe/fiscal. */
  peutRattacherFiscal?: boolean;
  /** fon.mutation:valider — l'avis se donne depuis la phase Validation. */
  peutValider?: boolean;
  /** cad.controle:traiter — la décision motivée se prend depuis la phase Contrôle. */
  peutTraiterAnomalie?: boolean;
}) {
  const [fiche, setFiche] = useState<any>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /**
   * ⚠️ « PROCÉDURES » EST PLACÉ EN DEUXIÈME, JUSTE APRÈS L'APERÇU, et c'est un
   * choix. C'est l'onglet qui répond à « que se passe-t-il sur ce fonds » —
   * la question qu'un agent se pose en ouvrant une parcelle. Les droits (onglet
   * Foncier) disent ce que le fonds EST ; les procédures disent ce qui lui
   * ARRIVE, et rien ne le montrait avant.
   */
  const [onglet, setOnglet] = useState<
    'apercu' | 'actions' | 'procedures' | 'impact' | 'foncier' | 'documents' | 'graphe' | 'historique'>('apercu');
  /**
   * §13 — « Parcelle → Documents », depuis la carte.
   *
   * ⚠️ AJOUTÉ DANS CE PANNEAU, PAS DANS UN SECOND. J'avais commencé à écrire un
   * `PanneauParcelle` neuf avant de constater que celui-ci existait, monté au
   * niveau de l'app et donc déjà affiché par-dessus la carte. Deux panneaux
   * contextuels pour la même parcelle auraient divergé au premier correctif —
   * c'est le motif que ce dépôt paie en boucle, et qui se règle en REGARDANT
   * avant d'écrire.
   */
  const [controlesPlan, setControlesPlan] = useState<ControlesPlan | null>(null);
  const [editionsPlan, setEditionsPlan] = useState<EditionsPlan | null>(null);

  const charger = useCallback(async () => {
    try {
      const f = await api.parcelles.fiche(identifiant);
      setFiche(f); setErreur(null);
    } catch (e) {
      setErreur(e instanceof ErreurApi
        ? (e.statut === 404 ? "Cette parcelle n'existe pas dans le registre." : e.message)
        : String(e));
    }
  }, [identifiant]);

  useEffect(() => {
    let vivant = true;
    setFiche(null); setErreur(null);
    api.parcelles.fiche(identifiant)
      .then((f) => { if (vivant) setFiche(f); })
      .catch((e) => {
        if (!vivant) return;
        setErreur(e instanceof ErreurApi
          ? (e.statut === 404 ? "Cette parcelle n'existe pas dans le registre." : e.message)
          : String(e));
      });
    return () => { vivant = false; };
  }, [identifiant]);

  /* La remontée de la section — voir `onSection` dans le contrat ci-dessus. */
  useEffect(() => {
    if (!onSection) return;
    const parc = fiche?.parcelle ?? {};
    onSection(parc.section && parc.circonscription_id
      ? { code: String(parc.section), circ: Number(parc.circonscription_id) }
      : null);
    return () => { onSection(null); };
  }, [fiche, onSection]);

  /* La remontée du titre — voir `onTitre` dans le contrat ci-dessus. */
  useEffect(() => {
    if (!onTitre) return;
    const d = fiche?.parcelle?.designation_cadastrale;
    onTitre(d?.referenceScellee ?? d?.designation ?? null);
    return () => { onTitre(null); };
  }, [fiche, onTitre]);

  if (erreur) return <p className="erreur">{erreur}</p>;
  if (!fiche) return <p className="attente">Chargement…</p>;

  const p = fiche.parcelle ?? {};
  const q = fiche.qualite ?? {};
  const etat = etatVerification(q);
  const droitsMasques = fiche.droits && !Array.isArray(fiche.droits);

  return (
    <>
      <div className={`bandeau-etat ${etat.classe}`}>
        <strong>{etat.libelle}</strong>
        <span>{etat.explication}</span>
      </div>

      {/**
        * ⚠️⚠️ UN RATTACHEMENT APPROCHÉ DOIT LE DIRE, ET DIRE DE COMBIEN.
        *
        * Mesuré le 22/09/2026 : 1 166 parcelles vivantes ont été rattachées à
        * « la section la plus proche » — dont **411 à plus d'un kilomètre et
        * 201 au-delà de cinq, jusqu'à 12,9 km**. À cette distance, la section
        * est dans un autre ressort. Sans cette mention, un agent ouvre la fiche,
        * lit une section, et n'a rien qui le détrompe : l'approximation se
        * présente exactement comme une mesure.
        *
        * ⚠️ ET CE N'EST PAS RÉPARABLE PAR UN MEILLEUR CALCUL. Ces parcelles
        * tombent hors de TOUT contour communal — 1 165 sur 1 166 n'ont aucune
        * division de niveau 4. Le sectionnement suit le découpage communal :
        * là où il n'y a pas de commune, il n'y a pas de section, et aucune
        * requête ne rattache à ce qui n'existe pas. Ce qui se corrige, c'est
        * le silence.
        */}
      {p.rattachement_methode === 'proximite' && (
        <div className="bandeau-etat ambre">
          <strong>Rattachement approché</strong>
          <span>
            {p.rattachement_distance_m !== null && p.rattachement_distance_m !== undefined
              ? `La section la plus proche est à ${formerDistance(p.rattachement_distance_m)}. `
              : 'La section a été choisie par proximité. '}
            Aucun contour communal ne couvre cet emplacement : ce rattachement situe,
            il ne fait pas ressort. À reprendre quand le découpage sera livré.
          </span>
        </div>
      )}

      <nav className="onglets">
        {(['apercu', 'actions', 'procedures', 'impact', 'foncier', 'documents', 'graphe', 'historique'] as const).map((o) => (
          <button key={o} className={onglet === o ? 'actif' : ''} onClick={() => setOnglet(o)}>
            {{ apercu: 'Aperçu', actions: 'Actions', procedures: 'Procédures', impact: 'Impact', foncier: 'Foncier',
               documents: 'Documents', graphe: 'Liens', historique: 'Historique' }[o]}
          </button>
        ))}
      </nav>

      {/**
        * ⚠️⚠️ L'ONGLET ACTIONS — CE QU'ON PEUT FAIRE ICI, ET POURQUOI PAS LE RESTE.
        *
        * Trois filtres, et l'ordre décide de ce qu'on dit à l'agent : l'OBJET
        * (cette parcelle s'y prête-t-elle ?), le MÉTIER (est-ce votre ressort ?),
        * l'HABILITATION (votre rôle l'autorise-t-il ?). Les trois produisent une
        * absence, et les trois appellent des gestes opposés.
        *
        * ⚠️ UNE ACTION N'EST JAMAIS ABSENTE EN SILENCE. Du ressort du métier,
        * elle est listée — offerte, ou refusée avec son motif. Une action qu'on
        * retire se lit « la fonction n'existe pas », et l'agent ne demande
        * jamais l'habilitation qui la débloquerait.
        */}
      {onglet === 'actions' && (() => {
        const etat: EtatParcelle = {
          statut: String(p.statut ?? q.statut ?? 'projet'),
          origine: (p.origine ?? q.origine ?? null) as string | null,
          droits: Array.isArray(fiche.droits) ? fiche.droits.length : 0,
          releves: Array.isArray(fiche.controles) ? fiche.controles.length : 0,
          verifiee: Boolean(q.verifie_le),
        };
        const verdicts = actionsPour(etat, metier, permissions);
        return (
          <section className="onglet-actions">
            {verdicts.length === 0 ? (
              <p className="indetermine">{motifListeVide(metier)}</p>
            ) : (
              <ul className="liste-actions">
                {verdicts.map((v) => (
                  <li key={v.action.cle} className={v.offerte ? 'offerte' : `refusee ${v.cause}`}>
                    <div className="action-tete">
                      <strong>{v.action.libelle}</strong>
                      <em>{v.action.aide}</em>
                    </div>
                    {v.offerte ? (
                      <button
                        type="button"
                        className="bouton"
                        /* ⚠️⚠️ LE BOUTON EMPORTE LA PARCELLE, ET C'EST TOUT
                           L'OBJET DU §2.3. Il appelait `onOuvrirRubrique(rubrique)`
                           SANS second argument : l'agent arrivait au guichet sur
                           un formulaire vide et retapait une adresse que le
                           registre connaît. Mesuré le 23/09/2026 — un dépôt a
                           déclaré « Conakry / Matoto / Tombola » pour un fonds de
                           Boké-Centre, et l'affaire est partie à 250 km. */
                        onClick={() => onOuvrirRubrique?.(v.action.rubrique, identifiant)}
                      >Ouvrir</button>
                    ) : (
                      /* ⚠️ LE MOTIF, PAS UN BOUTON GRISÉ. Un bouton désactivé
                         sans phrase force à deviner lequel des trois obstacles,
                         et on devine mal. */
                      <p className={v.cause === 'objet' ? 'indetermine' : 'masque'}>{v.motif}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {/* ⚠️ DIRE QUE LA LISTE DÉPEND DU MÉTIER. Sans cette ligne, deux
                agents comparant leurs écrans concluent à un défaut. */}
            <p className="carte-legende-note">
              Cette liste est celle de votre métier{metier ? ` (${metier})` : ''} :
              un autre métier en voit d’autres, sur la même parcelle.
            </p>
          </section>
        );
      })()}

      {onglet === 'apercu' && (
        <section>
          {/* ═══════════════════════════════════════════════════════════════
              ⚠️⚠️ LES RÉFÉRENCES CADASTRALES, TOUTES — demandées par le
              fondateur le 23/09/2026 : « on doit [voir] toutes les références
              cadastre, le secteur par exemple, que ce soit dans le panneau
              latéral ou dans le formulaire ».
              Ce bloc n'en montrait que TROIS : identifiant, n° cadastral,
              division. La SECTION et la CIRCONSCRIPTION — celles par lesquelles
              un agent désigne un fonds au guichet et sur un plan — n'étaient
              même pas servies par la fiche, alors que le registre les porte sur
              1 761 157 parcelles sur 1 761 157.
              ⚠️ LA RÉFÉRENCE HÉRITÉE DU REGISTRE PAPIER EST AFFICHÉE MÊME
              ABSENTE, et son absence est dite : un champ retiré parce qu'il est
              vide se lit « ce produit ne connaît pas cette notion ».
              ═══════════════════════════════════════════════════════════════ */}
          <Champs lignes={[
            /* ⚠️⚠️ LA DÉSIGNATION MÈNE — « tout doit être comme ça »
               (fondateur, 12/10/2026) : le fonds s'identifie par
               collectivité-section-numéro, scellé quand la référence est
               émise. GN-PAR-… descend au rang d'identifiant interne : aucun
               plan ne le porte. */
            ['Référence cadastrale', (() => {
              const d = (p as any).designation_cadastrale;
              if (d?.referenceScellee) return d.referenceScellee;
              if (d?.designation) return `${d.designation} — à sceller`;
              return 'sans numéro au plan : la désignation précède la référence';
            })()],
            ['Identifiant interne (registre)', p.identifiant],
            ['Section', p.section ?? '—'],
            ['N° dans la section', p.numero_cadastral ?? '—'],
            ['Circonscription', p.circonscription
              ? `${p.circonscription}${p.circonscription_code ? ` (${p.circonscription_code})` : ''}`
              : '—'],
            ['Division', p.division
              ? `${p.division}${p.division_code ? ` · ${p.division_code}` : ''}`
              : '—'],
            ['Réf. héritée (registre papier)',
              p.reference_cadastrale ?? 'aucune inscrite'],
            ['Statut', p.statut],
            ['Surface', metres(p.surface_m2)],
            ['Périmètre', p.perimetre_m ? `${Number(p.perimetre_m).toFixed(0)} m` : '—'],
            ['Version', p.version],
            ['Valide depuis', dateFr(p.valide_du)],
          ]} />

          {(() => {
            const d = (p as any).designation_cadastrale;
            if (d?.etat !== 'a_emettre') return null;
            return peutReferencer ? (
              <FormeReference identifiant={p.identifiant}
                designation={d.designation} onFait={() => void charger()} />
            ) : (
              <p className="mention">
                La référence se scelle d'office à l'immatriculation — ou par un
                agent porteur de <code>cad.parcelle:modifier</code>.
              </p>
            );
          })()}

          {/* ═══════════════════════════════════════════════════════════════
              LA PLANCHE DE LA SECTION, DEPUIS LA FICHE — demandé par le
              fondateur le 23/09/2026 : « on doit pouvoir aussi imprimer la
              section ».
              ⚠️ ELLE EXISTAIT DÉJÀ, ET C'EST LE DÉFAUT : le seul chemin pour y
              arriver passait par le panneau du plan cadastral — celui-là même
              qui, replié, était invisible. Une fonction qu'on ne peut atteindre
              que depuis un écran qu'on ne voit pas n'existe pas pour l'agent.
              ⚠️ LE BLOC NE DISPARAÎT JAMAIS EN SILENCE : sans section ou sans
              circonscription, il DIT pourquoi. Un bloc absent se lirait « ce
              produit n'imprime pas les sections ». */}
          <h3>Planche de la section <span className="aide">(plan cadastral)</span></h3>
          {p.section && p.circonscription_id ? (
            <ImprimerLaPlanche code={p.section} circ={Number(p.circonscription_id)} />
          ) : (
            <p className="mention">
              {!p.section
                ? 'Cette parcelle n’est rattachée à aucune section : il n’y a pas de planche à tirer. '
                  + 'Le rattachement se fait au plan cadastral.'
                : 'La section de cette parcelle n’est rattachée à aucune circonscription : '
                  + 'la planche ne peut pas être composée sans elle.'}
            </p>
          )}

          <h3>Adresse <span className="aide">indicative — calculée, non inscrite</span></h3>
          <BlocAdresse a={(fiche as any).adresse} />

          <h3>Qualité de la donnée <span className="aide">(§48)</span></h3>
          <Champs lignes={[
            ['Origine', q.origine],
            ['Précision', q.precision_m ? `± ${q.precision_m} m` : '—'],
            ['Méthode', q.methode ?? '—'],
            ['Source', q.source ?? '—'],
            ['Opposable', q.opposable ? 'oui' : 'non'],
          ]} />

          {/*
            ⚠️ DERNIER ÉCHELON DE LA DESCENTE — pays → commune → section →
            parcelle → BÂTIMENT. Le bâti n'était que listé, en mètres carrés :
            un nombre ne dit pas OÙ le bâtiment se trouve dans la parcelle, ni
            s'il en déborde — ce qui est précisément la question qu'on vient
            poser à cet écran.
          */}
          <TraceParcelle geometrie={(p as any).geometrie} batiments={fiche.batiments ?? []} />

          <h3>Bâti rattaché <span className="aide">({fiche.batiments?.length ?? 0})</span></h3>
          {fiche.batiments?.length
            ? <ul className="liste">{fiche.batiments.map((b: any) => (
                <li key={b.identifiant}><code>{b.identifiant}</code> · {metres(b.emprise_m2)} · {libelleUsageBati(b.usage)} · {b.niveaux != null ? `${b.niveaux} niv.` : 'niveaux inconnus'}</li>))}</ul>
            : <p className="vide">Aucune emprise rattachée.</p>}

          <h3>Mise au plan cadastral <span className="aide">(§5 — POST /parcelles/:identifiant/activer)</span></h3>
          {p.statut === 'active' ? (
            <p className="rien-a-signaler">Déjà inscrite au plan cadastral : cette parcelle est opposable.</p>
          ) : estEchafaudage(p.origine ?? q.origine) ? (
            <p className="indetermine">
              {REFUS_ACTIVATION[(p.origine ?? q.origine) as OrigineEchafaudage]}
            </p>
          ) : !peutActiver ? (
            <p className="masque">Votre rôle n'a pas la permission <code>cad.parcelle:modifier</code>.</p>
          ) : (
            <FormeActivation identifiant={identifiant} onFait={() => void charger()} />
          )}
        </section>
      )}

      {onglet === 'procedures' && (
        <section>
          {/* ⚠️ `onOuvrirDossier` EST LA PORTE QUI MANQUAIT. Sans lui,
              `procedures.tsx` n'affiche pas son bouton « Ouvrir le dossier » —
              le panneau nomme le dossier de l'affaire et n'y mène pas. */}
          <ChronologieJuridique identifiant={identifiant}
                                peutValider={peutValider}
                                peutTraiterAnomalie={peutTraiterAnomalie}
                                onOuvrirDossier={onOuvrirDossier} />
        </section>
      )}

      {onglet === 'impact' && <VoletImpact identifiant={identifiant} />}

      {onglet === 'foncier' && (
        <section>
          <h3>Droits</h3>
          {droitsMasques ? (
            <div className="masque">
              <strong>{fiche.droits.nombre} droit(s) — masqués</strong>
              <p>Votre habilitation ne donne pas accès à l'identité des titulaires
                 (motif : <code>{fiche.droits.motif}</code>). La parcelle existe et porte des droits.</p>
            </div>
          ) : fiche.droits?.length ? (
            <table className="tableau">
              <thead><tr><th>Nature</th><th>Titulaire</th><th>Quotité</th><th>Rang</th><th>Identité</th></tr></thead>
              <tbody>
                {fiche.droits.map((d: any, i: number) => (
                  <tr key={i}>
                    <td>{d.nature}</td>
                    <td>{d.denomination ?? '—'} <span className="aide">{d.type}</span></td>
                    {/* Fractions ENTIÈRES : 1/3 s'affiche 1/3, jamais 0,333. */}
                    <td><code>{d.quotite_num}/{d.quotite_den}</code></td>
                    <td>{d.rang ?? '—'}</td>
                    <td>{d.identite_verifiee ? '✔ vérifiée' : 'déclarée'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="vide">
              Aucun droit inscrit. <strong>Ce n'est pas « sans propriétaire »</strong> : c'est
              une parcelle que le registre ne rattache encore à personne.
            </p>
          )}

          <h3>Restrictions et servitudes</h3>
          {fiche.restrictions?.length
            ? <ul className="liste">{fiche.restrictions.map((r: any, i: number) => <li key={i}><strong>{r.type}</strong> — {r.description}</li>)}</ul>
            : <p className="vide">Aucune restriction inscrite.</p>}

          <h3>Filiation cadastrale <span className="aide">(§6)</span></h3>
          {fiche.filiation?.length
            ? <ul className="liste">{fiche.filiation.map((f: any, i: number) => (
                <li key={i}><code>{f.parent}</code> → <code>{f.enfant}</code> · {f.operation} · {dateFr(f.le)}</li>))}</ul>
            : <p className="vide">Parcelle d'origine : aucune division ni fusion enregistrée.</p>}

          <h3>Déposer une revendication <span className="aide">(§9 — un occupant se déclare)</span></h3>
          <p className="mention">
            Une revendication n'ouvre pas un droit : elle ouvre une instruction. Plusieurs
            revendications concurrentes sur la même parcelle sont un signal, pas une erreur.
          </p>
          <FormeRevendication identifiant={identifiant} onFait={() => void charger()} />
        </section>
      )}

      {/* ══ §13 — DOCUMENTS DE LA PARCELLE ══════════════════════════════════
          Fondateur : « Ajouter dans le Maps cadastral un panneau contextuel :
          Parcelle → Documents […] Générer plan cadastral · Voir les documents
          existants · Voir les versions ».

          ⚠️ CE PANNEAU EST DÉJÀ AFFICHÉ PAR-DESSUS LA CARTE — il est monté au
          niveau de l'app, pas dans une rubrique. Le geste décrit par le
          fondateur manquait donc d'un seul onglet, pas d'un écran : jusqu'ici,
          pour obtenir le plan de la parcelle qu'on avait sous les yeux, il
          fallait LIRE son numéro, changer d'écran et le RETAPER. */}
      {onglet === 'documents' && (
        <section>
          <SectionDocuments
            identifiant={identifiant}
            controles={controlesPlan} editions={editionsPlan}
            setControles={setControlesPlan} setEditions={setEditionsPlan}
          />
          {/* ⚠️ APRÈS le plan cadastral, dans l'ordre du §13. Et dans un bloc
              distinct : les deux documents ont des suites de versions séparées,
              les fondre ferait croire à un historique commun. */}
          <SectionSituation identifiant={identifiant} />
          {/* §5 — d'où vient ce terrain. En dernier : c'est le document le plus
              rare, et le seul qui regarde le PASSÉ de la parcelle. */}
          <SectionArpentage identifiant={identifiant} />
        </section>
      )}

      {onglet === 'graphe' && (
        <section>
          <h3>Biens immobiliers <span className="aide">(§10)</span></h3>
          {fiche.graphe?.biens_immobiliers?.length
            ? <ul className="liste">{fiche.graphe.biens_immobiliers.map((b: any, i: number) => (
                <li key={i}><code>{b.bien_id}</code> · <Etat v={b.statut} /> · {dateFr(b.verifie_le)}
                  {b.ecarts && <em> — écarts : {JSON.stringify(b.ecarts)}</em>}</li>))}</ul>
            : <p className="vide">Aucun bien immobilier rapproché.</p>}

          <h3>Objets fiscaux <span className="aide">(§13)</span></h3>
          {fiche.graphe?.objets_fiscaux?.length
            ? <ul className="liste">{fiche.graphe.objets_fiscaux.map((o: any, i: number) => (
                <li key={i}><code>{o.fiscal_property_id}</code> · <Etat v={o.statut} />
                  {o.ecart_surface_pct != null && ` · écart de surface ${Number(o.ecart_surface_pct).toFixed(1)} %`}</li>))}</ul>
            : <p className="vide">Aucun objet fiscal rapproché.</p>}

          <h3>Contrôles</h3>
          {fiche.controles?.length
            ? <ul className="liste">{fiche.controles.map((c: any, i: number) => (
                <li key={i}><strong>{c.regle}</strong> → <Etat v={c.resultat} />
                  <span className="aide"> {c.candidats_examines} candidat(s) examiné(s), {dateFr(c.evalue_le)}</span></li>))}</ul>
            : <p className="vide">Aucun contrôle enregistré.</p>}

          <h3>Rattacher un objet fiscal <span className="aide">(§13 — POST /graphe/fiscal)</span></h3>
          {!peutRattacherFiscal ? (
            <p className="masque">Votre rôle n'a pas la permission <code>lien.objet_fiscal:creer</code>.</p>
          ) : (
            <FormeRattachementFiscal identifiant={identifiant} onFait={() => void charger()} />
          )}
        </section>
      )}

      {onglet === 'historique' && (
        <section>
          <h3>Journal <span className="aide">(§47 — ajout seul, scellé)</span></h3>
          {fiche.historique?.length
            ? <ol className="chronologie">{fiche.historique.map((e: any, i: number) => (
                <li key={i}><time>{dateFr(e.horodatage)}</time><strong>{e.action}</strong>{e.motif && <p>{e.motif}</p>}</li>))}</ol>
            : <p className="vide">Aucun événement journalisé pour cette parcelle.</p>}
        </section>
      )}
    </>
  );
}

/** Un résultat de contrôle a QUATRE valeurs : les colorer sans les réduire. */
function Etat({ v }: { v: string }) {
  const classe = { verifie: 'vert', incoherent: 'rouge', declare: 'ambre', non_verifiable: 'gris' }[v] ?? 'gris';
  return <span className={`etat ${classe}`}>{v}</span>;
}

/**
 * §5 — POST /parcelles/:identifiant/activer.
 *
 * ⚠️ C'EST L'ACTE QUI COMPTE (commentaire du service, parcelles.ts:200). Tant
 * qu'une parcelle est en « projet », elle ne s'oppose à personne. Le motif est
 * obligatoire côté service (3 à 500 caractères) : on le valide ici pour ne pas
 * faire un aller-retour réseau juste pour l'apprendre.
 */
/**
 * LE SCELLEMENT DÉLIBÉRÉ — POST /parcelles/:identifiant/referencer.
 * ⚠️ Une référence ne se refrappe pas : le serveur refuse la seconde émission,
 * et son refus (409 nommé) s'affiche tel quel.
 */
function FormeReference({ identifiant, designation, onFait }: {
  identifiant: string; designation: string | null; onFait: () => void;
}) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  const sceller = async () => {
    setErreur(null); setSucces(null); setEnCours(true);
    try {
      const r = await api.parcelles.referencer(identifiant);
      setSucces(`Référence scellée : ${r.reference} — frappée sur l'ancrage du fonds, elle ne se refrappe pas.`);
      onFait();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  return (
    <div className="filtres">
      <button className="bouton primaire" disabled={enCours} onClick={() => void sceller()}>
        {enCours ? 'Scellement…' : `Sceller la référence — ${designation ?? identifiant}`}
      </button>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {succes && <p className="rien-a-signaler" role="status">{succes}</p>}
    </div>
  );
}

function FormeActivation({ identifiant, onFait }: { identifiant: string; onFait: () => void }) {
  const [motif, setMotif] = useState('');
  const [precisionM, setPrecisionM] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur(null); setSucces(null);
    if (motif.trim().length < 3) { setErreur('Le motif doit compter au moins 3 caractères.'); return; }
    setEnCours(true);
    try {
      const p = Number(precisionM);
      const r = await api.parcelles.activer(identifiant, {
        motif: motif.trim(),
        ...(precisionM.trim() && Number.isFinite(p) && p > 0 ? { precisionM: p } : {}),
      });
      setSucces(r.message ?? (r.absorption
        ? `Parcelle inscrite au plan. ${r.absorption}`
        : `Parcelle inscrite au plan cadastral (statut ${r.statut}).`));
      setMotif(''); setPrecisionM('');
      onFait();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  return (
    <form className="filtres" onSubmit={(e) => void soumettre(e)}>
      <label>Motif <span className="aide">(obligatoire, ≥ 3 caractères)</span>
        <input value={motif} onChange={(ev) => setMotif(ev.target.value)}
               placeholder="Ex. : levé contrôlé, aucun recouvrement bloquant" disabled={enCours} />
      </label>
      <label>Précision annoncée (m) <span className="aide">(optionnel)</span>
        <input value={precisionM} onChange={(ev) => setPrecisionM(ev.target.value)}
               type="number" min="0" step="0.01" disabled={enCours} />
      </label>
      <button className="bouton primaire" disabled={enCours || motif.trim().length < 3}>
        {enCours ? 'Activation…' : 'Activer'}
      </button>
      {erreur && <p className="erreur">{erreur}</p>}
      {succes && <p className="succes">{succes}</p>}
    </form>
  );
}

/**
 * §9 — POST /territoire/parcelles/:identifiant/revendication.
 *
 * ⚠️ Le service exige `qualite` (une des six valeurs) et `motif` (3 à
 * 300 caractères) ; `titulaireId` et `pieces` sont optionnels. Aucun de ces
 * noms n'est deviné : ils reproduisent exactement le schéma zod de
 * `src/modules/territoire.ts:286`.
 */
function FormeRevendication({ identifiant, onFait }: { identifiant: string; onFait: () => void }) {
  const QUALITES = ['occupant', 'heritier', 'acquereur', 'coutumier', 'locataire', 'autre'] as const;
  const [qualite, setQualite] = useState<typeof QUALITES[number]>('occupant');
  const [titulaireId, setTitulaireId] = useState('');
  const [pieces, setPieces] = useState('');
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [alerte, setAlerte] = useState<string | null>(null);

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur(null); setSucces(null); setAlerte(null);
    if (motif.trim().length < 3) { setErreur('Le motif doit compter au moins 3 caractères.'); return; }
    setEnCours(true);
    try {
      const r = await api.territoire.revendiquer(identifiant, {
        qualite,
        ...(titulaireId.trim() ? { titulaireId: titulaireId.trim() } : {}),
        pieces: pieces.split(',').map((s) => s.trim()).filter(Boolean),
        motif: motif.trim(),
      });
      setSucces(`Revendication déposée (${r.revendications_sur_cette_parcelle} sur cette parcelle). ${r.avertissement}`);
      if (r.alerte) setAlerte(r.alerte);
      setMotif(''); setTitulaireId(''); setPieces('');
      onFait();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  return (
    <form className="filtres" onSubmit={(e) => void soumettre(e)}>
      <label>Qualité du déclarant
        <select value={qualite} onChange={(ev) => setQualite(ev.target.value as typeof qualite)} disabled={enCours}>
          {QUALITES.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
      </label>
      <label>Titulaire déjà connu <span className="aide">(UUID, optionnel)</span>
        <input value={titulaireId} onChange={(ev) => setTitulaireId(ev.target.value)} placeholder="laisser vide si inconnu" disabled={enCours} />
      </label>
      <label>Pièces produites <span className="aide">(séparées par des virgules, optionnel)</span>
        <input value={pieces} onChange={(ev) => setPieces(ev.target.value)} placeholder="attestation, témoignage" disabled={enCours} />
      </label>
      <label>Motif <span className="aide">(obligatoire, ≥ 3 caractères)</span>
        <input value={motif} onChange={(ev) => setMotif(ev.target.value)} disabled={enCours} />
      </label>
      <button className="bouton" disabled={enCours || motif.trim().length < 3}>
        {enCours ? 'Dépôt…' : 'Déposer la revendication'}
      </button>
      {erreur && <p className="erreur">{erreur}</p>}
      {alerte && <p className="indetermine">{alerte}</p>}
      {succes && <p className="succes">{succes}</p>}
    </form>
  );
}

/**
 * §13 — POST /graphe/fiscal.
 *
 * ⚠️ CE N'EST PAS UN APPEL AU FISC : c'est un enregistrement de rapprochement.
 * Le service constate ce qu'il peut et écrit `non_verifiable` quand Mowibo
 * Fiscal ne répond pas (src/domaine/rapprochement.ts) — c'est un RÉSULTAT
 * valide, pas une panne, et l'écran ne le traite jamais comme une erreur.
 */
function FormeRattachementFiscal({ identifiant, onFait }: { identifiant: string; onFait: () => void }) {
  const [fiscalPropertyId, setFiscalPropertyId] = useState('');
  const [juridiction, setJuridiction] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [resultat, setResultat] = useState<ResultatRapprochementFiscal | null>(null);

  const uuidValide = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fiscalPropertyId.trim());

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur(null); setResultat(null);
    if (!uuidValide) { setErreur("L'identifiant d'objet fiscal doit être un UUID valide."); return; }
    setEnCours(true);
    try {
      const r = await api.graphe.rattacherFiscal({
        identifiantParcelle: identifiant,
        fiscalPropertyId: fiscalPropertyId.trim(),
        ...(juridiction.trim() ? { juridiction: juridiction.trim() } : {}),
      });
      setResultat(r);
      setFiscalPropertyId(''); setJuridiction('');
      onFait();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setEnCours(false); }
  };

  return (
    <>
      <form className="filtres" onSubmit={(e) => void soumettre(e)}>
        <label>Identifiant de l'objet fiscal <span className="aide">(UUID, obligatoire)</span>
          <input value={fiscalPropertyId} onChange={(ev) => setFiscalPropertyId(ev.target.value)}
                 placeholder="00000000-0000-0000-0000-000000000000" disabled={enCours} />
        </label>
        <label>Juridiction <span className="aide">(optionnel)</span>
          <input value={juridiction} onChange={(ev) => setJuridiction(ev.target.value)} disabled={enCours} />
        </label>
        <button className="bouton" disabled={enCours || !uuidValide}>
          {enCours ? 'Rapprochement…' : 'Rattacher'}
        </button>
      </form>
      {erreur && <p className="erreur">{erreur}</p>}
      {resultat && (
        <div className={resultat.verdict === 'verifie' ? 'rien-a-signaler'
                        : resultat.verdict === 'incoherent' ? 'avertissement' : 'indetermine'}>
          <p><Etat v={resultat.verdict} /> — {resultat.resume}</p>
          <p className="aide">
            {resultat.controlesExecutes} contrôle(s) exécuté(s) sur {resultat.controles.length},
            {' '}{resultat.controlesEnAnomalie} en anomalie
            {resultat.ecartSurfacePct !== null && ` · écart de surface ${resultat.ecartSurfacePct.toFixed(1)} %`}
          </p>
        </div>
      )}
    </>
  );
}

function EnTete({ titre, onFermer }: { titre: string; onFermer: () => void }) {
  return (
    <header className="panneau-entete">
      <h2><code>{titre}</code></h2>
      <button className="fermer" onClick={onFermer} aria-label="Fermer la fiche">×</button>
    </header>
  );
}

function Champs({ lignes }: { lignes: Array<[string, unknown]> }) {
  return (
    <dl className="champs">
      {lignes.map(([cle, valeur]) => (
        <div key={cle}><dt>{cle}</dt><dd>{valeur == null || valeur === '' ? '—' : String(valeur)}</dd></div>
      ))}
    </dl>
  );
}

/**
 * ADRESSE INDICATIVE (§9, §56).
 *
 * ⚠️ L'ÉTIQUETTE FAIT PARTIE DE LA DONNÉE. Une adresse affichée nue à côté de
 * l'identifiant cadastral se lit comme un attribut du registre — or elle est
 * calculée par Mowibo Maps à la lecture, elle n'est inscrite nulle part, et
 * elle changera si la voie est renommée. Un notaire qui la recopierait dans un
 * acte croirait citer le cadastre.
 *
 * ⚠️ ET LA DISTANCE À LA VOIE EST RENDUE AVEC L'ADRESSE, jamais ailleurs.
 * 3 mètres, c'est une adresse ; 400 mètres, c'est le nom de la route la plus
 * proche d'un terrain qui n'y touche pas. Reléguée dans un détail repliable,
 * cette nuance ne serait jamais lue.
 */
function BlocAdresse({ a }: { a: any }) {
  if (!a) {
    return <p className="vide">Adresse non demandée pour cette parcelle.</p>;
  }
  if (a.degrade || !a.adresse) {
    // Dire POURQUOI il n'y a pas d'adresse. « — » se lit « cette parcelle n'a
    // pas d'adresse », ce qui est faux : c'est la source qui n'a pas répondu.
    const raisons: Record<string, string> = {
      limite_de_debit: "Mowibo Maps a limité le débit — ce n'est pas une absence d'adresse.",
      injoignable: "Mowibo Maps est injoignable depuis le service.",
    };
    return (
      <p className="vide">
        {raisons[a.degrade] ?? `Adresse non obtenue (${a.degrade ?? 'aucun résultat'}).`}
      </p>
    );
  }
  const loin = a.voie?.distanceM != null && a.voie.distanceM > 100;
  return (
    <>
      <Champs lignes={[
        ['Adresse', a.adresse],
        ['Voie', a.voie ? `${a.voie.nom}${a.voie.distanceM != null ? ` — à ${Math.round(a.voie.distanceM)} m` : ''}` : '—'],
        ['Quartier', a.quartier ?? '—'],
        /* ⚠️ LA VILLE, qu'aucun champ ne montrait. Maps la rend dans `localite`
           avec sa distance : à 2 km ce n'est pas l'adresse de la parcelle, c'est
           le bourg dont elle dépend. On donne la distance avec le nom, sans quoi
           « Marella » se lit comme une adresse. */
        ['Localité', a.localite
          ? `${a.localite.nom}${a.localite.distanceM != null ? ` — à ${Math.round(a.localite.distanceM)} m` : ''}`
          : '—'],
        /* ⚠️ DEUX ÉTAGES, PAS UN. 36 communes urbaines et 304 sous-préfectures :
           n'afficher que « Commune » laissait vide l'immense majorité du pays. */
        ['Commune', a.commune ?? '—'],
        ['Sous-préfecture', a.sousPrefecture ?? '—'],
        ['Préfecture', a.prefecture ?? '—'],
        ['Région', a.region ?? '—'],
        ['Code postal', a.codePostal ?? '—'],
      ]} />
      {loin && (
        <p className="erreur">
          La voie la plus proche passe à {Math.round(a.voie.distanceM)} m : cette adresse
          situe le secteur, elle ne désigne pas la parcelle.
        </p>
      )}
      {a.reperes?.length > 0 && (
        <p className="mention">
          Repères : {a.reperes.map((r: any) => `${r.nom} (${Math.round(r.distanceM)} m)`).join(' · ')}
        </p>
      )}
      <p className="mention">
        Calculée par Mowibo Maps à l'ouverture de cette fiche. <strong>Le registre
        n'inscrit aucune adresse</strong> — une parcelle s'identifie par son
        identifiant cadastral, jamais par une voie qui se renomme.
      </p>
    </>
  );
}

/**
 * §13 + §11 + §16 — ce que la parcelle a délivré, et ce qu'elle peut délivrer.
 *
 * ⚠️ IL N'APPELLE QUE LES ROUTES DU PLAN, il ne recompose aucun verdict. Deux
 * surfaces qui calculent chacune leur « prêt à générer » finissent par en
 * afficher deux différents, et c'est celui qu'on ne regarde pas qui se trompe.
 */

/**
 * §3 — LE PLAN DE SITUATION, DEPUIS LA CARTE.
 *
 * ⚠️ UN BLOC SÉPARÉ, PAS UNE VARIANTE DU PLAN CADASTRAL. Les deux documents
 * n'ont ni le même contenu, ni la même suite de versions : « V2 » de l'un et
 * « V2 » de l'autre désignent deux papiers différents. Les fondre dans une
 * liste unique ferait croire à un historique commun.
 *
 * ⚠️ ET L'AGENT VOIT CE QUE LE PLAN MONTRERA AVANT DE L'IMPRIMER — les trois
 * échelles retenues, le nombre d'objets par couche, et surtout les couches
 * TRONQUÉES. Un document d'État qui omet 77 % des parcelles voisines doit le
 * dire à l'écran comme il le dit sur le papier.
 */
function SectionSituation({ identifiant }: { identifiant: string }) {
  const [controles, setControles] = useState<ControlesSituation | null>(null);
  const [editions, setEditions] = useState<EditionsSituation | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setControles(null); setEditions(null); setErreur(null);
    // ⚠️ `allSettled` : une liste d'éditions en échec ne doit pas faire
    // disparaître les contrôles, sans quoi l'agent conclut que la parcelle
    // n'a rien.
    Promise.allSettled([
      api.situation.controles(identifiant),
      api.situation.editions(identifiant),
    ]).then(([c, e]) => {
      if (c.status === 'fulfilled') setControles(c.value);
      else setErreur(String((c.reason as Error)?.message ?? c.reason));
      if (e.status === 'fulfilled') setEditions(e.value);
    });
  }, [identifiant]);

  const bloque = controles?.etat === 'bloque';
  /**
   * ⚠️ UN BOUTON QUI REND 503 EST PIRE QUE PAS DE BOUTON. La route refuse de
   * délivrer si le moteur d'impression n'est pas configuré ; l'écran le sait
   * d'avance et le dit, au lieu de laisser l'agent découvrir l'échec après
   * avoir promis un document à un usager.
   */
  const sansMoteur = controles !== null && !controles.rendudisponible;
  const indisponible = bloque || sansMoteur;
  const url = api.situation.url(identifiant);

  return (
    <>
      <div className="champ-titre">Plan de situation</div>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {!controles && !erreur && <p className="mention">Contrôles en cours…</p>}

      {controles && (
        <>
          <p className={bloque ? 'erreur' : 'succes'} role={bloque ? 'alert' : undefined}>
            <strong>{bloque ? 'GÉNÉRATION BLOQUÉE' : 'PRÊT À GÉNÉRER'}</strong>
            {bloque && ` — ${controles.motifs.length} contrôle(s) non satisfait(s)`}
          </p>
          {bloque && <ul className="motifs">{controles.motifs.map((m) => <li key={m}>{m}</li>)}</ul>}
          {sansMoteur && !bloque && (
            <p className="erreur" role="alert">
              Le moteur d'impression n'est pas configuré : le document ne peut pas être
              délivré. Un aperçu reste consultable à l'écran.
            </p>
          )}

          <ul className="cadres-situation">
            {controles.cadres.map((c) => (
              <li key={c.cle}>
                <strong>{c.titre}</strong> · 1:{c.echelle.toLocaleString('fr-FR')}
                {c.parcelleHorsEchelle && (
                  <span className="mention"> — parcelle figurée par un repère, non à l'échelle</span>
                )}
                <ul className="couches">
                  {c.couches.map((k) => (
                    <li key={k.cle} className={k.tronquee ? 'alerte-trace' : undefined}>
                      {k.objets.toLocaleString('fr-FR')} × {k.libelle}
                      {/* ⚠️ « TRONQUÉE » NE SE DEVINE PAS D'UN NOMBRE : 200 objets
                          se lisent « il y en a 200 », jamais « il y en a 885 dont
                          685 ne sont pas dessinés ». */}
                      {k.tronquee && ' — TRONQUÉE : le registre en contient davantage'}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          {controles.reserves.length > 0 && (
            <>
              <p className="mention">Le document portera ces réserves :</p>
              <ul className="reserves">{controles.reserves.map((r) => <li key={r}>{r}</li>)}</ul>
            </>
          )}
          {controles.couchesAbsentes.length > 0 && (
            <>
              <p className="mention">Non figuré, faute de donnée au registre :</p>
              <ul className="reserves">
                {controles.couchesAbsentes.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </>
          )}

          <a className={`bouton primaire${indisponible ? ' desactive' : ''}`}
             href={indisponible ? undefined : url} target="_blank" rel="noreferrer"
             aria-disabled={indisponible}
             onClick={(e) => { if (indisponible) e.preventDefault(); }}>
            Générer le plan de situation
          </a>
          <a className="bouton" href={api.situation.url(identifiant, { apercu: true })}
             target="_blank" rel="noreferrer">
            Aperçu à l'écran
          </a>
        </>
      )}

      {editions && editions.editions.length === 0 && (
        <p className="vide">Aucun plan de situation délivré pour cette parcelle.</p>
      )}
      {editions && editions.editions.length > 0 && (
        <ul className="liste-editions">
          {editions.editions.map((e) => (
            <li key={e.version} data-trace={e.traceInchange ? 'inchange' : 'modifie'}>
              <strong>V{e.version}</strong> · {e.echelles.map((x) => `1:${x.toLocaleString('fr-FR')}`).join(' · ')}
              <br />
              <span className="mention">
                {new Date(e.editeLe).toLocaleString('fr-FR')} — {e.document}
              </span>
              <br />
              <span className={e.traceInchange ? 'mention' : 'alerte-trace'}>
                {e.traceInchange
                  ? `Tracé inchangé depuis (géométrie v${e.versionGeometrie}).`
                  : `⚠ La parcelle a été redessinée depuis : ce plan figure le tracé `
                    + `v${e.versionGeometrie}, qui n'est plus celui du registre.`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}


/**
 * §5 — LE DOCUMENT D'ARPENTAGE, DEPUIS LA CARTE.
 *
 * ⚠️ IL N'A PAS DE VERDICT « PRÊT / BLOQUÉ », et l'écran ne doit pas en
 * inventer un. Les contrôles du §14 portent sur la capacité à DESSINER une
 * parcelle ; un document d'arpentage dessine un PASSAGE entre deux états, et
 * son défaut typique n'est pas une géométrie invalide mais une opération dont
 * le registre ne sait dire ni l'auteur ni la justification. Ce manque-là ne se
 * bloque pas — il s'imprime, et l'écran le montre AVANT le clic.
 *
 * ⚠️ « AUCUNE PARCELLE ANTÉRIEURE » N'EST PAS UNE ERREUR quand l'opération est
 * une création : c'est une immatriculation, le cas le plus normal du registre.
 * Les deux se ressemblent trait pour trait si l'écran ne les distingue pas.
 */
function SectionArpentage({ identifiant }: { identifiant: string }) {
  const [etat, setEtat] = useState<EtatArpentage | null>(null);
  const [editions, setEditions] = useState<EditionsArpentage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setEtat(null); setEditions(null); setErreur(null);
    Promise.allSettled([
      api.arpentage.etat(identifiant),
      api.arpentage.editions(identifiant),
    ]).then(([e, v]) => {
      if (e.status === 'fulfilled') setEtat(e.value);
      else setErreur(String((e.reason as Error)?.message ?? e.reason));
      if (v.status === 'fulfilled') setEditions(v.value);
    });
  }, [identifiant]);

  const sansMoteur = etat !== null && !etat.rendudisponible;
  const nb = (v: number) => v.toLocaleString('fr-FR');

  return (
    <>
      <div className="champ-titre">Document d'arpentage</div>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {!etat && !erreur && <p className="mention">Lecture des filiations…</p>}

      {etat && (
        <>
          <p><strong>{etat.operationLibelle}</strong>
            {etat.operationLe && (
              <span className="mention"> — {new Date(etat.operationLe).toLocaleDateString('fr-FR')}</span>
            )}
          </p>

          {etat.operation === 'creation' ? (
            <p className="mention">
              Immatriculation : cette parcelle n'a aucune parcelle antérieure au registre.
              L'état avant du document sera légitimement vide.
            </p>
          ) : (
            <ul className="arp-bilan">
              <li>Avant : <strong>{nb(etat.surfaceAvantM2)} m²</strong> · {etat.avant.length} parcelle(s)</li>
              <li>Après : <strong>{nb(etat.surfaceApresM2)} m²</strong> · {etat.apres.length} parcelle(s)</li>
            </ul>
          )}

          {etat.avant.length > 0 && (
            <ul className="arp-parents">
              {etat.avant.map((p) => (
                <li key={p.identifiant}>
                  {p.identifiant} · {nb(p.surfaceM2)} m²
                  {/* ⚠️ LA PART ABSORBÉE CHANGE LE SENS DE LA LIGNE. Une maille
                      « absorbée à 50 % » n'apporte que la moitié de sa surface :
                      sans cette mention, la somme affichée paraîtrait fausse. */}
                  {p.partAbsorbeePct !== null && (
                    <span className="mention"> — absorbée à {p.partAbsorbeePct} %</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* ⚠️⚠️ LE CONSTAT QUE CE DOCUMENT EXISTE POUR PRODUIRE. Un sol
              d'arrivée sans origine au registre est le signal que la finalité
              anti-fraude vise. Il est affiché AVANT l'édition, pas découvert
              dans le PDF. */}
          {etat.manques.length > 0 && (
            <>
              <p className="mention">Ce document ne pourra PAS établir :</p>
              <ul className="motifs">{etat.manques.map((m) => <li key={m}>{m}</li>)}</ul>
            </>
          )}
          {etat.reserves.length > 0 && (
            <ul className="reserves">{etat.reserves.map((r) => <li key={r}>{r}</li>)}</ul>
          )}
          {sansMoteur && (
            <p className="erreur" role="alert">
              Le moteur d'impression n'est pas configuré : le document ne peut pas être
              délivré. Un aperçu reste consultable.
            </p>
          )}

          <a className={`bouton primaire${sansMoteur ? ' desactive' : ''}`}
             href={sansMoteur ? undefined : api.arpentage.url(identifiant)}
             target="_blank" rel="noreferrer" aria-disabled={sansMoteur}
             onClick={(e) => { if (sansMoteur) e.preventDefault(); }}>
            Générer le document d'arpentage
          </a>
          <a className="bouton" href={api.arpentage.url(identifiant, { apercu: true })}
             target="_blank" rel="noreferrer">
            Aperçu à l'écran
          </a>
        </>
      )}

      {editions && editions.editions.length === 0 && (
        <p className="vide">Aucun document d'arpentage délivré pour cette parcelle.</p>
      )}
      {editions && editions.editions.length > 0 && (
        <ul className="liste-editions">
          {editions.editions.map((e) => (
            <li key={e.version} data-trace={e.apresInchange === false ? 'modifie' : 'inchange'}>
              <strong>V{e.version}</strong> · {e.operationLibelle}
              <br />
              <span className="mention">
                {new Date(e.editeLe).toLocaleString('fr-FR')} — {e.document}
              </span>
              {e.apresInchange === false && (
                <>
                  <br />
                  <span className="alerte-trace">
                    ⚠ L'état d'arrivée a changé depuis : ce document décrit une étape
                    qui n'est plus la dernière.
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function SectionDocuments({
  identifiant, controles, editions, setControles, setEditions,
}: {
  identifiant: string;
  controles: ControlesPlan | null;
  editions: EditionsPlan | null;
  setControles: (v: ControlesPlan | null) => void;
  setEditions: (v: EditionsPlan | null) => void;
}) {
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setControles(null); setEditions(null); setErreur(null);
    /**
     * ⚠️ `allSettled`, PAS `all`. Les deux appels sont indépendants : si la
     * liste des éditions échoue, les contrôles doivent quand même s'afficher.
     * Un `all` ferait disparaître toute la section à cause d'une seule route,
     * et l'agent conclurait que la parcelle n'a rien.
     */
    Promise.allSettled([
      api.plans.controles(identifiant),
      api.plans.editions(identifiant),
    ]).then(([c, e]) => {
      if (c.status === 'fulfilled') setControles(c.value);
      else setErreur(String((c.reason as Error)?.message ?? c.reason));
      if (e.status === 'fulfilled') setEditions(e.value);
    });
  }, [identifiant, setControles, setEditions]);

  const bloque = controles?.etat === 'bloque';
  const url = api.plans.url(identifiant, { variante: 'cadastral', format: 'a4_paysage' });

  return (
    <>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {!controles && !erreur && <p className="mention">Contrôles en cours…</p>}

      {controles && (
        <>
          {/* Le verdict AVANT le bouton, et en toutes lettres : la couleur ne
              peut pas le porter seule. */}
          <p className={bloque ? 'erreur' : 'succes'} role={bloque ? 'alert' : undefined}>
            <strong>{bloque ? 'GÉNÉRATION BLOQUÉE' : 'PRÊT À GÉNÉRER'}</strong>
            {bloque && ` — ${controles.motifs.length} contrôle(s) non satisfait(s)`}
          </p>
          {bloque && <ul className="motifs">{controles.motifs.map((m) => <li key={m}>{m}</li>)}</ul>}
          {controles.reserves.length > 0 && (
            <>
              <p className="mention">Le document portera ces réserves :</p>
              <ul className="reserves">{controles.reserves.map((r) => <li key={r}>{r}</li>)}</ul>
            </>
          )}
          <a className={`bouton primaire${bloque ? ' desactive' : ''}`}
             href={bloque ? undefined : url} target="_blank" rel="noreferrer"
             aria-disabled={bloque}
             onClick={(e) => { if (bloque) e.preventDefault(); }}>
            Générer le plan cadastral
          </a>
        </>
      )}

      <div className="champ-titre">Versions délivrées</div>
      {!editions && !erreur && <p className="mention">Chargement…</p>}
      {editions && editions.editions.length === 0 && (
        /* ⚠️ « AUCUNE ÉDITION » N'EST PAS « AUCUN PLAN N'A ÉTÉ DÉLIVRÉ ».
           Avant le 14/09/2026 les plans n'étaient pas archivés : une parcelle a
           pu être imprimée cent fois sans laisser de trace. La route le dit,
           l'écran le répète — sinon on conclut d'un vide qu'il ne s'est rien
           passé. */
        <p className="vide">{editions.lecture}</p>
      )}
      {editions && editions.editions.length > 0 && (
        <ul className="liste-editions">
          {editions.editions.map((e) => (
            <li key={`${e.variante}-${e.version}`}
                data-trace={e.trace_inchange ? 'inchange' : 'modifie'}>
              <strong>V{e.version}</strong> · {e.variante} · 1:{e.echelle}
              <br />
              <span className="mention">
                {new Date(e.edite_le).toLocaleString('fr-FR')} — {e.document}
              </span>
              <br />
              {/* ⚠️ CETTE PHRASE NE DIT PAS QU'UN PLAN EST FAUX : elle dit que
                  la parcelle a BOUGÉ depuis son édition. C'est l'événement même
                  que le §11 demande de rendre visible. */}
              <span className={e.trace_inchange ? 'mention' : 'alerte-trace'}>
                {e.trace_inchange
                  ? `Tracé inchangé depuis (géométrie v${e.version_geometrie}).`
                  : `⚠ La parcelle a été redessinée depuis : ce plan figure le tracé `
                    + `v${e.version_geometrie}, qui n'est plus celui du registre.`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * VOLET IMPACT — « qu'est-ce qui casse si les actes en cours s'appliquent ? »
 *
 * ⚠️ IL EST DANS UN ONGLET, ET C'EST UNE DÉCISION TECHNIQUE, PAS D'ERGONOMIE.
 * Le service prend, pour projeter, LES MÊMES VERROUS QU'UNE ÉCRITURE : il
 * applique l'effet pour de vrai, puis annule. Charger ces projections à
 * l'ouverture de la fiche mettrait chaque consultation en concurrence avec les
 * actes réels du registre. On ne projette donc que si l'agent le demande.
 *
 * ⚠️⚠️ UN ENSEMBLE VIDE NE SE LIT PAS « RIEN NE CASSE ». Une parcelle sans acte
 * en cours n'a pas un impact nul : elle n'a pas d'acte. Les deux se
 * ressembleraient trait pour trait dans un panneau muet — et sur un registre
 * foncier, « aucun impact » lu à la place de « aucun acte » est le contresens
 * qui fait valider un dossier qu'on n'a pas regardé. Le volet le DIT.
 *
 * ⚠️ L'affichage lui-même vient de `./projection`, partagé avec `affaire.tsx`.
 * En écrire une seconde version ici donnerait deux implémentations d'une même
 * règle ; elles divergeraient au premier correctif appliqué à une seule.
 */
function VoletImpact({ identifiant }: { identifiant: string }) {
  const [procedures, setProcedures] = useState<ProcedureParcelle[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(true);

  useEffect(() => {
    let vivant = true;
    setOccupe(true); setErreur(null); setProcedures(null);
    api.parcelles.procedures(identifiant)
      .then((r) => { if (vivant) setProcedures(r.procedures ?? []); })
      .catch((e) => {
        if (!vivant) return;
        setErreur(e instanceof ErreurApi && e.estRefus
          ? "Votre habilitation ne couvre pas les procédures de ce fonds : "
            + "l'impact des actes en cours ne vous est pas montré."
          : String((e as Error).message ?? e));
      })
      .finally(() => { if (vivant) setOccupe(false); });
    return () => { vivant = false; };
  }, [identifiant]);

  // ⚠️ `applique_le` NON NUL = l'acte a DÉJÀ produit ses effets. Le projeter
  // rendrait `deja_appliquee` : un panneau qui n'apprend rien et qui aurait
  // pris des verrous pour le dire.
  const enCours = (procedures ?? []).filter((p) => p.acte && !p.acte.applique_le);

  return (
    <section>
      <h3>Ce que les actes en cours feront au registre</h3>

      {occupe && <p className="vide">Lecture des procédures du fonds…</p>}
      {erreur && <p className="note-indispo">{erreur}</p>}

      {!occupe && !erreur && procedures !== null && procedures.length === 0 && (
        <p className="vide">
          Aucune procédure n'a jamais été ouverte sur ce fonds — il n'y a donc rien à projeter.
          Ce n'est pas « aucun impact » : c'est « aucun acte ».
        </p>
      )}

      {!occupe && !erreur && procedures !== null && procedures.length > 0 && enCours.length === 0 && (
        <p className="vide">
          Les {procedures.length} procédure(s) de ce fonds sont closes ou déjà appliquées :
          leurs effets sont au registre, ils ne sont plus à projeter.
        </p>
      )}

      {enCours.map((p) => (
        <SectionProjection
          key={p.acte!.reference}
          reference={p.acte!.reference}
          titre={`${p.libelle} — ${p.acte!.reference}`}
        />
      ))}
    </section>
  );
}
